/**
 * HomePage.js — Real-time attendance dashboard
 *
 * The central screen of Haziri App. Shows a live snapshot of all workers'
 * attendance status for today, updated in real time via WebSocket.
 *
 * Layout (desktop ≥720px: two-column grid):
 *   Left column  (.left-col)  — Snapshot hero with count bars and chips
 *   Right column (.right-col) — Searchable, filterable, sortable worker list
 *
 * State machine:
 *   attendanceList is the single source of truth, hydrated by the WebSocket:
 *   - 'init' event   → replace entire list (initial load)
 *   - 'create' event → append new records
 *   - 'update' event → merge changed records by ID (immutable update)
 *
 * Derived state (via useMemo — no extra API calls):
 *   counts   — per-status totals for the snapshot
 *   filtered — searched + filtered + sorted view over attendanceList
 *
 * Status normalisation:
 *   Directus lifecycle: unmarked | present | break | absent | checkout | leave | weekly_off
 *   Approval lifecycle: attendance.is_approved (boolean, separate from status)
 *   Display groups (normalizeStatus output):
 *     pending = unmarked
 *     ended   = checkout (+ legacy 'approved' records during migration)
 *     leave   = leave
 *     off     = weekly_off
 *   This lets the filter bar show a simpler taxonomy while keeping full
 *   Directus values available for action routing in ActionFooter.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showAlert } from '../components/UI';
import { playSuccessSound, playErrorSound } from '../utils/audio';
import DayDrawer from '../components/DayDrawer';
import PaymentModal from '../components/PaymentModal';
import {
  startShift, endShift, startBreak, endBreak,
  markAbsent, markAbsentWithReason,
  resetAttendance, createAttendanceWS, refreshSession, getPowerDays,
} from '../api/directus';
import { formatDuration, parseTimeToDate } from '../utils/helpers';
import { useNotifications } from '../hooks/useNotifications';

const BASE_URL = process.env.REACT_APP_DIRECTUS_URL || 'https://staff.innomalous.com';

// ── Module-level helpers ───────────────────────────────────────────────────────

/**
 * Build a sized, authenticated Directus asset URL.
 *
 * `photo` can be a UUID string (relation not expanded) or an object with an `id`
 * field (expanded via *.*.*). Both resolve to the same /assets/{uuid} path.
 *
 * Auth:
 *   Dev  — session cookies don't work cross-origin to staff.innomalous.com, so we
 *           append the dev access token as a query param.
 *   Prod — both subdomains share innomalous.com (same-site), so the httpOnly
 *           session cookie is sent automatically with image requests.
 */
function buildPhotoUrl(photo, px = 112) {
  if (!photo) return null;
  const uuid = typeof photo === 'string' ? photo : photo.id;
  if (!uuid) return null;
  const url = `${BASE_URL}/assets/${uuid}?width=${px}&height=${px}&fit=cover`;
  if (process.env.NODE_ENV === 'development') {
    const token = localStorage.getItem('dev_access_token');
    if (token) return `${url}&access_token=${token}`;
  }
  return url;
}

/**
 * Collapse Directus lifecycle statuses → display groups.
 * ActionFooter uses raw status + is_approved — normalizeStatus is only for
 * display, filtering, and snapshot counts.
 * Legacy 'approved' status maps to 'ended' for backwards compat during migration.
 */
function normalizeStatus(s) {
  if (s === 'unmarked') return 'pending';
  if (s === 'checkout' || s === 'approved') return 'ended';
  if (s === 'weekly_off') return 'off';
  return s; // present | break | absent | leave
}

/**
 * Derive initials from a name string.
 * Single-word names return one letter; multi-word names return first + last initial.
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Map a worker name to a hue value (0–359) for deterministic avatar colours.
 * Uses a polynomial rolling hash so the same name always produces the same hue,
 * and different names are spread around the colour wheel.
 * The result is used with oklch() to generate accessible, visually distinct colours.
 */
function nameToHue(name) {
  let hash = 0;
  for (const c of (name || '')) hash = (hash * 31 + c.charCodeAt(0)) & 0xffff;
  return hash % 360;
}

/** Format an ISO timestamp as a localised 12-hour time string for display banners. */
function formatTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Human-readable labels for the absent_reason enum stored in Directus. */
const ABSENT_REASON_LABELS = {
  personal: 'Personal',
  medical:  'Medical',
  family:   'Family Emergency',
  noshow:   'No-show',
  other:    'Other',
};

/** Visual metadata for each display status — icon uses Material Symbols Rounded codepoints. */
const STATUS_META = {
  present: { label: 'Present',     icon: 'check_circle',       cls: 'pill-present' },
  break:   { label: 'On Break',    icon: 'pause_circle',       cls: 'pill-break'   },
  absent:  { label: 'Absent',      icon: 'cancel',             cls: 'pill-absent'  },
  pending: { label: 'Not Started', icon: 'schedule',           cls: 'pill-pending' },
  ended:   { label: 'Shift Ended', icon: 'history_toggle_off', cls: 'pill-ended'   },
  leave:   { label: 'On Leave',    icon: 'beach_access',       cls: 'pill-leave'   },
  off:     { label: 'Weekly Off',  icon: 'event_busy',         cls: 'pill-off'     },
};

/**
 * Default sort order for the worker list — active workers first, ended/pending last.
 * Within the same status, workers are sorted alphabetically.
 */
const STATUS_ORDER = ['present', 'break', 'pending', 'absent', 'leave', 'off', 'ended'];

// ── Module-scope sub-components ───────────────────────────────────────────────
// Defined here (not inside HomePage) so React sees a stable component type
// across re-renders. Inner-defined components get a new identity every render,
// which forces unmount → remount and replays entry animations on every WS update.

/**
 * A button that requires a sustained press (default 2.5 s) before firing its
 * action, preventing accidental punches on touch devices.
 *
 * While held, a translucent fill sweeps left-to-right inside the button as
 * visual progress feedback. Releasing before the fill completes cancels the
 * action and snaps the fill back instantly.
 *
 * Uses pointer events so it works identically for mouse and touch input.
 * `touch-action: none` (set in CSS via .hold-btn) prevents scroll interference.
 */
function HoldButton({ className = '', onAction, children, disabled, duration = 500 }) {
  const [holding, setHolding] = useState(false);
  const timerRef = useRef(null);

  function begin(e) {
    if (disabled) return;
    e.preventDefault(); // suppress browser's own long-press haptic / text-selection vibration
    setHolding(true);
    timerRef.current = setTimeout(() => {
      setHolding(false);
      onAction?.();
    }, duration);
  }

  function cancel() {
    clearTimeout(timerRef.current);
    setHolding(false);
  }

  return (
    <button
      className={`${className} hold-btn${holding ? ' holding' : ''}`}
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onTouchStart={e => e.preventDefault()}
      onContextMenu={e => e.preventDefault()}
      disabled={disabled}
    >
      <span className="hold-fill" aria-hidden="true" />
      {children}
    </button>
  );
}

/** Small count chip used inside Snapshot. */
function ChipStat({ label, count, dot }) {
  return (
    <div className="chip">
      <div className={`chip-dot ${dot}`} />
      <div className="chip-num num">{count}</div>
      <span className="chip-label">{label}</span>
    </div>
  );
}

const SNAPSHOT_SEGS = [
  { key: 'present', cls: 'c-present' },
  { key: 'break',   cls: 'c-break'   },
  { key: 'absent',  cls: 'c-absent'  },
  { key: 'leave',   cls: 'c-leave'   },
  { key: 'off',     cls: 'c-off'     },
  { key: 'pending', cls: 'c-pending' },
  { key: 'ended',   cls: 'c-ended'   },
];

/**
 * Hero section showing present count, progress bar, and status chips.
 * isPowerDay: true when today is listed as an approved power day — shows ⚡ next to the date.
 */
function Snapshot({ wsConnected, onReconnect, counts, total, isPowerDay }) {
  const today = new Date();
  const dayName = today.toLocaleDateString('en-IN', { weekday: 'long' });
  const dayDate = today.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  // Portal-based tooltip — renders at document.body so it escapes snapshot's overflow:hidden
  const [powerDayTip, setPowerDayTip] = React.useState(null); // null = hidden; {top,left} = visible
  const powerDayBtnRef = React.useRef(null);

  React.useEffect(() => {
    if (!powerDayTip) return;
    function dismiss(e) {
      if (!powerDayBtnRef.current?.contains(e.target)) setPowerDayTip(null);
    }
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [powerDayTip]);

  return (
    <section className="snapshot fade-up">
      {/* Live/Offline indicator — shows WebSocket connection state */}
      <div className="snapshot-eyebrow">
        <span className={`live-dot${wsConnected ? '' : ' disconnected'}`} aria-hidden="true" />
        <span>{wsConnected ? 'Live' : 'Offline'} · {dayName} {dayDate}</span>
        {isPowerDay && (
          <>
            <button
              ref={powerDayBtnRef}
              className="p-sched-btn"
              style={{ marginLeft: 4 }}
              aria-label="Power Day"
              onClick={() => {
                if (powerDayTip) { setPowerDayTip(null); return; }
                const r = powerDayBtnRef.current.getBoundingClientRect();
                setPowerDayTip({ top: r.top - 6, left: r.left + r.width / 2 });
              }}
            >⚡</button>
            {powerDayTip && ReactDOM.createPortal(
              <div className="p-sched-tip" style={{
                position: 'fixed', bottom: 'auto',
                top: powerDayTip.top, left: powerDayTip.left,
                transform: 'translate(-50%, -100%)',
              }}>Power Day</div>,
              document.body
            )}
          </>
        )}
        {!wsConnected && (
          <button className="reconnect-btn" onClick={onReconnect} title="Reconnect">
            <span className="material-symbols-rounded">refresh</span>
            Reconnect
          </button>
        )}
      </div>

      <div className="snapshot-stat">
        <span className="snapshot-num num">{counts.present}</span>
        <span className="snapshot-of num">/ {total}</span>
      </div>
      <p className="snapshot-label">
        <strong>{counts.present} on the floor</strong>
        {counts.pending > 0 && <> · {counts.pending} yet to clock in</>}
      </p>

      {/* Proportional stack bar — segments hidden when count is 0 to avoid tiny slivers */}
      {total > 0 && (
        <div className="stack-bar" aria-hidden="true">
          {SNAPSHOT_SEGS.filter(s => counts[s.key] > 0).map(s => (
            <span
              key={s.key}
              className={s.cls}
              style={{ width: `${(counts[s.key] / total) * 100}%` }}
              title={`${STATUS_META[s.key]?.label}: ${counts[s.key]}`}
            />
          ))}
        </div>
      )}

      <div className="chips">
        <ChipStat label="On Break"   count={counts.break}  dot="c-break"  />
        <ChipStat label="Absent"     count={counts.absent} dot="c-absent" />
        <ChipStat label="Weekly Off" count={counts.off}    dot="c-off"    />
        <ChipStat label="On Leave"   count={counts.leave}  dot="c-leave"  />
      </div>
    </section>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function HomePage() {
  const { currentUser, logout, isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isSupported: notifSupported, isSubscribed, isLoading: notifLoading, error: notifError, enable: enableNotifications } = useNotifications();
  // Show notification errors as alerts so failures are never silent
  React.useEffect(() => {
    if (notifError) showAlert(notifError, 'error');
  }, [notifError]);

  // Live attendance data populated and updated by WebSocket events
  const [attendanceList, setAttendanceList] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const socketRef = useRef(null);          // active WebSocket instance
  const reconnectDelayRef = useRef(1000);  // current backoff delay in ms (doubles on each failure, capped at 30 s)
  const reconnectTimerRef = useRef(null);  // pending reconnect setTimeout handle
  const unmountingRef    = useRef(false);  // true once the component begins unmounting

  // Filter/sort/search UI state
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('name');
  const [navOpen, setNavOpen] = useState(false);

  // Modal state: null = closed, object = open with that record
  const [detailRecord, setDetailRecord] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);

  // Approved power days — fetched once on mount for the date badge and bonus logic
  const [powerDays, setPowerDays] = useState([]);

  // ── WebSocket connection ────────────────────────────────────────────────────

  /**
   * Opens a fresh WebSocket subscription for today's attendance.
   *
   * Before opening, the session is refreshed so the Directus auth handshake
   * succeeds even when the phone has been idle for a while. Any previously
   * open socket is torn down first; its onClose is suppressed by nulling
   * socketRef before calling close() so we don't trigger a duplicate reconnect.
   *
   * On unexpected close the handler schedules a retry with exponential backoff
   * (1 s → 2 s → 4 s … capped at 30 s). Manual reconnect (Reconnect button or
   * tab-visibility change) cancels the timer and connects immediately.
   */
  const connectWS = useCallback(async () => {
    // Cancel any pending auto-reconnect so we don't double-connect
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Tear down the previous socket; null socketRef first so its onClose
    // guard sees a stale reference and skips the reconnect schedule
    const prev = socketRef.current;
    socketRef.current = null;
    if (prev) prev.close();

    // Refresh the session before connecting — this is the main reason
    // the Reconnect button used to silently fail on mobile: an expired
    // session cookie caused the WS auth to be rejected immediately
    try { await refreshSession(); } catch {}

    if (unmountingRef.current) return;

    const socket = createAttendanceWS(new Date(), {
      onConnect: () => {
        setWsConnected(true);
        reconnectDelayRef.current = 1000; // successful connect → reset backoff
      },
      onClose: () => {
        // Ignore close events from replaced sockets (guard prevents duplicate reconnects)
        if (socketRef.current !== socket) return;
        setWsConnected(false);
        if (unmountingRef.current) return;
        // Schedule reconnect with exponential backoff
        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * 2, 30_000);
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (!unmountingRef.current) connectWS();
        }, delay);
      },
      onError: () => {
        if (socketRef.current === socket) setWsConnected(false);
      },
      onMessage: (data) => {
        if (data.event === 'init') {
          // Full snapshot on first connect — replace the list entirely
          setAttendanceList(data.data || []);
        } else if (data.event === 'create') {
          setAttendanceList(prev => [...prev, ...(data.data || [])]);
        } else if (data.event === 'update') {
          // Merge updated records by ID; unchanged records are kept as-is
          const map = {};
          (data.data || []).forEach(u => { map[u.id] = u; });
          setAttendanceList(prev => prev.map(item => map[item.id] ? map[item.id] : item));
        }
      },
    });
    socketRef.current = socket;
  }, []); // eslint-disable-line

  useEffect(() => {
    unmountingRef.current = false;
    connectWS();
    return () => {
      unmountingRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
    };
  }, [connectWS]);

  // Reconnect immediately when the tab becomes visible — the OS often closes
  // idle WebSocket connections when the screen locks or the user switches apps
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible' && !wsConnected) connectWS();
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [connectWS, wsConnected]);

  // Load approved power days once — list changes rarely so a single fetch on mount is sufficient
  useEffect(() => {
    getPowerDays().then(r => setPowerDays(r.data || [])).catch(() => {});
  }, []);

  // ── Action dispatcher ───────────────────────────────────────────────────────

  /**
   * Generic wrapper for attendance state-machine API calls.
   * WebSocket events will update the UI automatically after the server commits
   * the change — no local state mutation needed here.
   */
  async function doAction(fn, args, successMsg) {
    try {
      const res = await fn(...args);
      showAlert(res?.message || successMsg, 'success');
      playSuccessSound();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Action failed', 'error');
      playErrorSound();
    }
  }

  // ── Derived state (memoised) ────────────────────────────────────────────────

  /** Count workers in each display-status group. Runs only when the list changes. */
  const counts = useMemo(() => {
    const c = { present: 0, break: 0, absent: 0, pending: 0, ended: 0, leave: 0, off: 0 };
    for (const item of attendanceList) {
      const s = normalizeStatus(item.status);
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [attendanceList]);

  /** Apply search, filter, and sort over attendanceList. */
  const filtered = useMemo(() => {
    let list = attendanceList;
    const q = searchText.trim().toLowerCase();
    // Only filter on 2+ characters to avoid filtering on the first keystroke
    if (q.length >= 2) list = list.filter(item => item.worker?.name?.toLowerCase().includes(q));
    if (filter !== 'all') list = list.filter(item => normalizeStatus(item.status) === filter);
    if (sort === 'name') {
      list = [...list].sort((a, b) => (a.worker?.name || '').localeCompare(b.worker?.name || ''));
    } else if (sort === 'balance') {
      list = [...list].sort((a, b) => (b.worker?.current_balance || 0) - (a.worker?.current_balance || 0));
    } else {
      // Default: sort by STATUS_ORDER, then alphabetically within the same status
      list = [...list].sort((a, b) => {
        const ai = STATUS_ORDER.indexOf(normalizeStatus(a.status));
        const bi = STATUS_ORDER.indexOf(normalizeStatus(b.status));
        return ai - bi || (a.worker?.name || '').localeCompare(b.worker?.name || '');
      });
    }
    return list;
  }, [attendanceList, searchText, filter, sort]);

  const firstName = currentUser?.first_name || 'there';
  const total = attendanceList.length;

  // ISO date string for today (UTC) — matches how Directus stores the date field
  const todayStr        = new Date().toISOString().slice(0, 10);
  const todayIsPowerDay = powerDays.some(pd => pd.date === todayStr);


  /** Horizontal scrollable filter pill bar — one button per status group. */
  function FilterBar() {
    const items = [
      { key: 'all',     label: 'All',         count: total          },
      { key: 'present', label: 'Present',      count: counts.present },
      { key: 'break',   label: 'On Break',     count: counts.break   },
      { key: 'absent',  label: 'Absent',       count: counts.absent  },
      { key: 'leave',   label: 'On Leave',     count: counts.leave   },
      { key: 'off',     label: 'Weekly Off',   count: counts.off     },
      { key: 'pending', label: 'Pending',      count: counts.pending },
      { key: 'ended',   label: 'Ended',        count: counts.ended   },
    ];
    return (
      <div className="dash-filters">
        {items.map(i => (
          <button
            key={i.key}
            className="dash-filter"
            aria-pressed={filter === i.key}
            onClick={() => setFilter(i.key)}
          >
            <span>{i.label}</span>
            <span className="fcount num">{i.count}</span>
          </button>
        ))}
      </div>
    );
  }

  /** Sort dropdown with outside-click dismissal. Rendered via portal to escape overflow clipping. */
  function SortMenu() {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const btnRef  = useRef(null);
    const menuRef = useRef(null);

    function toggle() {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
      setOpen(o => !o);
    }

    useEffect(() => {
      if (!open) return;
      const fn = (e) => {
        if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', fn);
      return () => document.removeEventListener('mousedown', fn);
    }, [open]);

    const labels = { name: 'Name', status: 'Status', balance: 'Balance' };
    return (
      <>
        <button ref={btnRef} className="sort-btn" onClick={toggle}>
          <span className="material-symbols-rounded">sort</span>
          <span>{labels[sort]}</span>
        </button>
        {open && pos && ReactDOM.createPortal(
          <div ref={menuRef} className="dash-popover" style={{ position: 'fixed', top: pos.top, right: pos.right, minWidth: 160, zIndex: 500 }}>
            {Object.entries(labels).map(([k, label]) => (
              <button key={k} onClick={() => { setSort(k); setOpen(false); }}
                      style={{ fontWeight: sort === k ? 600 : 400 }}>
                <span className="material-symbols-rounded">
                  {sort === k ? 'check' : 'radio_button_unchecked'}
                </span>
                {label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </>
    );
  }

  /**
   * Individual worker attendance card.
   *
   * Sections (top to bottom):
   *   1. Top row  — avatar + name/balance + status pill + quick action buttons
   *   2. Banner   — contextual clock-in/out time or status message
   *   3. Footer   — primary action buttons (status-dependent, see ActionFooter)
   *
   * Avatar colour is derived deterministically from the worker name so the
   * same worker always gets the same colour across sessions.
   */
  function WorkerCard({ item }) {
    const { id, status, worker } = item;
    const displayStatus = normalizeStatus(status);
    const meta = STATUS_META[displayStatus];
    const hue = nameToHue(worker?.name);
    const initials = getInitials(worker?.name);
    const photoUrl = buildPhotoUrl(worker?.photo, 112);
    const balValue = worker?.current_balance || 0;
    const bal = Math.round(balValue).toLocaleString('en-IN', { maximumFractionDigits: 0 });
    const clockInTime  = formatTime(item.punch_in?.timestamp);
    const clockOutTime = formatTime(item.punch_out?.timestamp);

    // ── Live clock for present / break workers ────────────────────────────────
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
      if (displayStatus !== 'present' && displayStatus !== 'break') return;
      const id = setInterval(() => setNow(Date.now()), 60_000);
      return () => clearInterval(id);
    }, [displayStatus]);

    // ── Present: elapsed and net work minutes ─────────────────────────────────
    const punchInMs   = item.punch_in?.timestamp ? new Date(item.punch_in.timestamp).getTime() : 0;
    const elapsedMin  = punchInMs ? Math.max(0, Math.floor((now - punchInMs) / 60_000)) : null;
    const netWorkMin  = elapsedMin !== null ? elapsedMin - (item.break_duration || 0) : null;

    // ── Break: current break start + duration totals ──────────────────────────
    // break_out is null while the break is still active
    const currentBreak    = item.breaks?.find(b => !b.break_out);
    const breakStartTs    = currentBreak?.break_in?.timestamp;
    const breakStartTime  = formatTime(breakStartTs);
    const currentBreakMin = breakStartTs
      ? Math.max(0, Math.floor((now - new Date(breakStartTs).getTime()) / 60_000))
      : null;
    // break_duration only sums completed breaks; add the ongoing one for the total
    const totalBreakMin = currentBreakMin !== null
      ? (item.break_duration || 0) + currentBreakMin
      : null;

    // ── Ended: use flow-computed durations (populated on checkout) ────────────
    const shiftDurationMin = item.shift_duration ?? null;
    const netWorkEndedMin  = shiftDurationMin !== null
      ? shiftDurationMin - (item.break_duration || 0)
      : null;

    // ── Pending: show scheduled shift start time ──────────────────────────────
    const shiftStartTime = item.shift?.in_time
      ? formatTime(parseTimeToDate(item.shift.in_time))
      : null;

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const popRef  = useRef(null);
    const trigRef = useRef(null);

    function openMenu() {
      const rect = trigRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
      setMenuOpen(o => !o);
    }

    // Close context menu when clicking outside both the trigger and the popover
    useEffect(() => {
      if (!menuOpen) return;
      const fn = (e) => {
        if (popRef.current && !popRef.current.contains(e.target) &&
            trigRef.current && !trigRef.current.contains(e.target)) {
          setMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', fn);
      return () => document.removeEventListener('mousedown', fn);
    }, [menuOpen]);

    return (
      <article className="worker">
        {/* ── Top row ── */}
        <div className="worker-row">
          {/* Photo when available; hue-derived colour fallback for workers without one */}
          <div
            className="wk-avatar"
            style={photoUrl ? {} : {
              background: `oklch(86% 0.045 ${hue})`,
              color: `oklch(32% 0.07 ${hue})`,
            }}
          >
            {photoUrl
              ? <img src={photoUrl} alt={worker?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials
            }
          </div>

          <div className="worker-meta">
            <h3 className="worker-name">
              {worker?.name}
              {item.is_powerDay && <span className="wk-powerday-badge" title="Power Day">⚡</span>}
            </h3>
            <div className="worker-sub">
              <span
                className="wk-balance num"
                style={balValue < 0 ? { color: '#8a2f1a' } : undefined}
              >₹{bal}/-</span>
            </div>
          </div>

          <div className="row-pill">
            <span className={`pill ${meta?.cls}`}>
              <span className="material-symbols-rounded">{meta?.icon}</span>
              {meta?.label}
            </span>
          </div>

          {/* Quick actions: view shift (full) + view profile (icon) + context menu */}
          <div className="row-quick">
            <div className="row-actions">
              <button
                className="icon-btn"
                title="View shift detail"
                onClick={() => setDetailRecord(item)}
              >
                <span className="material-symbols-rounded">receipt_long</span>
              </button>
              {/* View profile promoted to quick-action — more useful than call for daily supervision */}
              <button
                className="icon-btn"
                title="View profile"
                onClick={() => navigate(`/worker/${worker?.id}`, { state: { from: pathname } })}
              >
                <span className="material-symbols-rounded">person</span>
              </button>
              <button
                ref={trigRef}
                className="icon-btn"
                title="More"
                onClick={openMenu}
                aria-expanded={menuOpen}
              >
                <span className="material-symbols-rounded">more_horiz</span>
              </button>

              {/* Context menu — rendered via portal so it escapes overflow:hidden on the worker card */}
              {menuOpen && menuPos && ReactDOM.createPortal(
                <>
                  <div className="dash-scrim" onClick={() => setMenuOpen(false)} />
                  <div ref={popRef} className="dash-popover" style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 500 }}>
                    {/* Call moved from quick-action row into dropdown — less frequent than view profile */}
                    <a href={`tel:${worker?.phone}`} onClick={() => setMenuOpen(false)}>
                      <span className="material-symbols-rounded">call</span> Call
                    </a>
                    <button onClick={() => { setDetailRecord(item); setMenuOpen(false); }}>
                      <span className="material-symbols-rounded">edit_note</span> Add note
                    </button>
                    {/* Pay advance — manager only; disabled (not hidden) so supervisors see it exists */}
                    <button
                      disabled={!isManager}
                      onClick={() => { setPaymentTarget(item); setMenuOpen(false); }}
                    >
                      <span className="material-symbols-rounded">currency_rupee</span> Record payment
                    </button>
                    <hr />
                    {/* Reset — admin only; dangerous, shown in red */}
                    <button
                      className="pop-danger"
                      disabled={!isAdmin}
                      onClick={() => { doAction(resetAttendance, [id], 'Reset done'); setMenuOpen(false); }}
                    >
                      <span className="material-symbols-rounded">cancel</span> Reset
                    </button>
                  </div>
                </>,
                document.body
              )}
            </div>
          </div>
        </div>

        {/* ── Contextual banner — status-specific meta info ── */}
        {displayStatus === 'present' && clockInTime && (
          <div className="worker-banner banner-present">
            <span className="banner-left">
              <span className="material-symbols-rounded">login</span>
              Clocked in at {clockInTime}
            </span>
            {netWorkMin !== null && (
              <span className="banner-right">{formatDuration(netWorkMin)}</span>
            )}
          </div>
        )}
        {displayStatus === 'break' && (
          <div className="worker-banner banner-break">
            <span className="banner-left">
              <span className="material-symbols-rounded">pause_circle</span>
              On break since {breakStartTime || '—'}
            </span>
            {currentBreakMin !== null && (
              <span className="banner-right">
                {formatDuration(currentBreakMin)}
                <span className="banner-secondary"> · {formatDuration(totalBreakMin)}</span>
              </span>
            )}
          </div>
        )}
        {displayStatus === 'absent' && (
          <div className="worker-banner banner-absent">
            <span className="banner-left">
              <span className="material-symbols-rounded">info</span>
              {ABSENT_REASON_LABELS[item.absent_reason] || item.absent_reason || 'Marked absent'}
              {!item.is_approved && ' · Pending approval'}
            </span>
          </div>
        )}
        {displayStatus === 'pending' && (
          <div className="worker-banner banner-pending">
            <span className="banner-left">
              <span className="material-symbols-rounded">schedule</span>
              Yet to clock in{shiftStartTime ? ` · expected ${shiftStartTime}` : ''}
            </span>
          </div>
        )}
        {displayStatus === 'ended' && clockOutTime && (
          <div className="worker-banner banner-ended">
            <span className="banner-left">
              <span className="material-symbols-rounded">history_toggle_off</span>
              Clocked out at {clockOutTime}
              {!item.is_approved && ' · Pending approval'}
            </span>
            {netWorkEndedMin !== null && (
              <span className="banner-right">{formatDuration(netWorkEndedMin)}</span>
            )}
          </div>
        )}
        {displayStatus === 'leave' && (
          <div className="worker-banner banner-leave">
            <span className="banner-left">
              <span className="material-symbols-rounded">beach_access</span>
              {item.leave_type?.name || 'On leave'}{!item.is_approved && ' · Pending approval'}
            </span>
          </div>
        )}
        {displayStatus === 'off' && (
          <div className="worker-banner banner-off">
            <span className="banner-left">
              <span className="material-symbols-rounded">event_busy</span>
              Weekly off{!item.is_approved && ' · Pending approval'}
            </span>
          </div>
        )}

        {/* ── Action footer — buttons determined by raw Directus status ── */}
        <ActionFooter item={item} displayStatus={displayStatus} />
      </article>
    );
  }

  /**
   * Renders the bottom action row of a worker card.
   * Switches on the raw Directus status for lifecycle actions; uses item.is_approved
   * for the approval state so checkout/absent/leave/weekly_off can all be approved.
   */
  function ActionFooter({ item, displayStatus }) {
    const { id, status } = item;
    const [absentPickerOpen, setAbsentPickerOpen] = useState(false);
    const [pendingReason, setPendingReason] = useState('');

    switch (status) {
      case 'present':
        return (
          <div className="worker-actions">
            {/* End shift on the left — less frequent, so moved to secondary position */}
            <HoldButton className="wa-primary" onAction={() => doAction(endShift, [id], 'Shift ended')}>
              <span className="material-symbols-rounded">logout</span> End shift
            </HoldButton>
            {/* Start break on the right — most-used action during the day, right = primary thumb zone */}
            <HoldButton className="wa-warm" onAction={() => doAction(startBreak, [id], 'Break started')}>
              <span className="material-symbols-rounded">pause_circle</span> Start break
            </HoldButton>
          </div>
        );
      case 'break':
        return (
          <div className="worker-actions">
            <HoldButton className="wa-primary" onAction={() => doAction(endShift, [id], 'Shift ended')}>
              <span className="material-symbols-rounded">logout</span> End shift
            </HoldButton>
            {/* Yellow for both start and end break — consistent break colour coding */}
            <HoldButton className="wa-warm" onAction={() => doAction(endBreak, [id], 'Break ended')}>
              <span className="material-symbols-rounded">play_circle</span> End break
            </HoldButton>
          </div>
        );
      case 'unmarked':
        if (absentPickerOpen) {
          return (
            <div className="worker-actions absent-picker">
              <select
                className="absent-reason-select"
                value={pendingReason}
                onChange={e => setPendingReason(e.target.value)}
                autoFocus
              >
                <option value="">Select reason…</option>
                <option value="personal">Personal</option>
                <option value="medical">Medical</option>
                <option value="family">Family Emergency</option>
                <option value="noshow">No-show</option>
                <option value="other">Other</option>
              </select>
              <div className="absent-picker-btns">
                <button className="wa-quiet" onClick={() => { setAbsentPickerOpen(false); setPendingReason(''); }}>
                  Cancel
                </button>
                {/* Regular button — the reason picker already provides sufficient confirmation friction */}
                <button
                  className="wa-absent"
                  onClick={() => {
                    doAction(markAbsentWithReason, [id, pendingReason || null], 'Marked absent');
                    setAbsentPickerOpen(false);
                    setPendingReason('');
                  }}
                >
                  <span className="material-symbols-rounded">cancel</span> Confirm
                </button>
              </div>
            </div>
          );
        }
        return (
          <div className="worker-actions">
            {/* Mark absent just opens the picker — no hold needed */}
            <button className="wa-quiet" onClick={() => setAbsentPickerOpen(true)}>
              <span className="material-symbols-rounded">cancel</span> Mark absent
            </button>
            {/* Green for start shift — positive, matches present status colour */}
            <HoldButton className="wa-green" onAction={() => doAction(startShift, [id], 'Shift started')}>
              <span className="material-symbols-rounded">login</span> Start shift
            </HoldButton>
          </div>
        );
      case 'absent':
        // Approved absent record — locked, view only
        if (item.is_approved) {
          return (
            <div className="worker-actions single">
              <button className="wa-quiet" onClick={() => setDetailRecord(item)}>
                <span className="material-symbols-rounded">receipt_long</span> View record
              </button>
            </div>
          );
        }
        return (
          <div className={`worker-actions${isManager ? '' : ' single'}`}>
            {/* Reversing absent is still a punch — hold required */}
            <HoldButton className="wa-quiet" onAction={() => doAction(startShift, [id], 'Shift started')}>
              <span className="material-symbols-rounded">undo</span> Reverse · clock in
            </HoldButton>
            {/* Manager can approve absent records — opens DayDrawer */}
            {isManager && (
              <button className="wa-primary" onClick={() => setDetailRecord(item)}>
                <span className="material-symbols-rounded">check_circle</span> Approve
              </button>
            )}
          </div>
        );
      case 'checkout':
        // Approved checkout — locked after manager sign-off
        if (item.is_approved) {
          return (
            <div className="worker-actions single">
              <button className="wa-quiet" onClick={() => setDetailRecord(item)}>
                <span className="material-symbols-rounded">receipt_long</span> View today's summary
              </button>
            </div>
          );
        }
        return (
          <div className="worker-actions single">
            {/* Opens DayDrawer for approval — no hold, approval happens inside the drawer */}
            <button
              className="wa-primary"
              disabled={!isManager}
              onClick={() => setDetailRecord(item)}
            >
              <span className="material-symbols-rounded">check_circle</span>
              {isManager ? 'Approve' : 'Pending approval'}
            </button>
          </div>
        );
      // Legacy status value on records created before the is_approved migration
      case 'approved':
        return (
          <div className="worker-actions single">
            <button className="wa-quiet" onClick={() => setDetailRecord(item)}>
              <span className="material-symbols-rounded">receipt_long</span> View today's summary
            </button>
          </div>
        );
      case 'leave':
      case 'weekly_off': {
        const recordLabel = status === 'leave' ? 'leave record' : 'weekly off record';
        if (item.is_approved) {
          return (
            <div className="worker-actions single">
              <button className="wa-quiet" onClick={() => setDetailRecord(item)}>
                <span className="material-symbols-rounded">receipt_long</span> View {recordLabel}
              </button>
            </div>
          );
        }
        return (
          <div className="worker-actions single">
            <button
              className="wa-primary"
              disabled={!isManager}
              onClick={() => setDetailRecord(item)}
            >
              <span className="material-symbols-rounded">check_circle</span>
              {isManager ? 'Approve' : 'Pending approval'}
            </button>
          </div>
        );
      }
      default:
        return null;
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="app">
      {/* ── Header ── */}
      <header className="app-header">
        <div className="greet">
          <span className="wave" aria-hidden="true">👋</span>
          <div className="greet-text">
            <h1>Hello {firstName}</h1>
            <small>{currentUser?.role_name}</small>
          </div>
        </div>
        <div className="header-actions">
          {notifSupported && (
            isSubscribed ? (
              // Static indicator — not a button. Users stay subscribed.
              <div className="icon-btn notif-btn notif-btn--on" aria-label="Notifications active" title="Notifications are on for this device">
                <span className="material-symbols-rounded">notifications_active</span>
                <span className="notif-dot" aria-hidden="true" />
              </div>
            ) : (
              <button
                className="icon-btn notif-btn"
                aria-label="Enable notifications"
                disabled={notifLoading}
                onClick={enableNotifications}
              >
                <span className="material-symbols-rounded">notifications</span>
              </button>
            )
          )}
          <button className="icon-btn" aria-label="Menu" onClick={() => setNavOpen(true)}>
            <span className="material-symbols-rounded">menu</span>
          </button>
        </div>
      </header>

      {/* ── Left column: snapshot ── */}
      <div className="left-col">
        <Snapshot wsConnected={wsConnected} onReconnect={connectWS} counts={counts} total={total} isPowerDay={todayIsPowerDay} />
      </div>

      {/* ── Right column: worker list ── */}
      <div className="right-col">
        {/* Search + sort toolbar */}
        <div className="dash-toolbar fade-up" style={{ animationDelay: '0.05s' }}>
          <div className="dash-search">
            <span className="material-symbols-rounded">search</span>
            <input
              placeholder="Search worker…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="icon-btn"
                style={{ width: 28, height: 28, border: 0 }}
                onClick={() => setSearchText('')}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
          </div>
          <SortMenu />
        </div>

        {/* Status filter chips */}
        <FilterBar />

        {/* List header: current filter label + count */}
        <div className="list-meta">
          <h2>{filter === 'all' ? "Today's roster" : (STATUS_META[filter]?.label || filter)}</h2>
          <span className="list-count num">{filtered.length} of {total}</span>
        </div>

        {/* Worker cards */}
        {filtered.length === 0 ? (
          <div className="dash-empty">
            <span className="material-symbols-rounded">person_search</span>
            <p>{attendanceList.length === 0 ? 'Connecting…' : 'No workers match your filter.'}</p>
          </div>
        ) : (
          <div className="worker-list stagger">
            {filtered.map(item => (
              <WorkerCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {/* ── Navigation modal (hamburger menu) ── */}
      {navOpen && (
        <div className="modal-overlay" onMouseDown={() => setNavOpen(false)}>
          <div className="modal modal-sm" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              Menu
              <button className="btn-icon" onClick={() => setNavOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn btn-ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { navigate('/workers'); setNavOpen(false); }}
              >
                <span className="material-symbols-rounded">group</span>
                View All Workers
              </button>
              <button
                className="btn btn-primary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { navigate('/new-worker'); setNavOpen(false); }}
              >
                + Add New Worker
              </button>
              <button
                className="btn btn-ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => { logout(); setNavOpen(false); }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {detailRecord && (
        <DayDrawer
          record={detailRecord}
          onClose={() => setDetailRecord(null)}
          onApproved={() => {}} // WebSocket update handles the card refresh automatically
        />
      )}
      <PaymentModal
        open={!!paymentTarget}
        workerId={paymentTarget?.worker?.id}
        workerName={paymentTarget?.worker?.name}
        onClose={() => setPaymentTarget(null)}
        onSaved={() => {}} // Balance update arrives via WebSocket worker.current_balance field
      />
    </div>
  );
}
